import React from "react";
import { useLocation } from "react-router-dom";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import {
  IconChevronRight,
  IconCreditCard,
  IconEdit2,
  IconPlus,
  IconTag,
  IconTrash2,
  IconWallet,
  IconX,
  IconZap,
} from "../components/common/WireframeIcons";
import { PatternFieldHelp } from "../components/common/PatternFieldHelp";
import { useAuth } from "../contexts/AuthContext";
import { useBilling } from "../contexts/BillingContext";
import { PlanLimitModal } from "../components/billing/PlanLimitModal";
import { FloatingToast } from "../components/common/FloatingToast";
import { apiRequest, ApiError } from "../api/client";
import {
  Account,
  AccountType,
  Category,
  CategoryKind,
  CategoryRule,
  CategoryRuleApi,
  CreditCard,
} from "../types/dashboard";
import { getPlanLimitDetails, getPlanLimitLabel } from "../utils/billing";
import { BillingMeResponse, CheckoutSessionSyncResponse, SubscriptionSyncResponse, PlanLimitDetails } from "../types/billing";

type SetupStep = "accounts" | "categories" | "uploads";

interface StatementImport {
  id: number;
  filename: string;
  notes: string | null;
  imported_at: string;
}

interface ImportResponse {
  statement: StatementImport;
  imported: number;
  skippedDuplicates?: number;
  overwrite?: {
    year: number;
    deletedTransactions: number;
    deletedImports: number;
  };
}

interface CardImport {
  id: number;
  card_id: number;
  filename: string;
  notes: string | null;
  imported_at: string;
}

interface CardImportResponse {
  statement: CardImport;
  imported: number;
  skippedDuplicates?: number;
  overwrite?: {
    year: number;
    deletedTransactions: number;
    deletedImports: number;
  };
}

const ruleAmountFormatter = new Intl.NumberFormat("es-ES", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const emptyInitialRule = () => ({ pattern: "", minAmount: "", maxAmount: "" });
const defaultTransactionDate = () => new Date().toISOString().slice(0, 10);

const CATEGORY_COLOR_OPTIONS: Record<CategoryKind, string[]> = {
  expense: [
    "#2563eb",
    "#0ea5e9",
    "#f59e0b",
    "#f97316",
    "#ef4444",
    "#ec4899",
    "#8b5cf6",
    "#6366f1",
  ],
  income: ["#15803d", "#16a34a", "#22c55e", "#4ade80", "#86efac"],
  transfer: ["#475569", "#64748b", "#94a3b8", "#cbd5f5", "#e2e8f0"],
};

const getDefaultCategoryColor = (kind: CategoryKind) => CATEGORY_COLOR_OPTIONS[kind][0];

const normalizeColorForKind = (kind: CategoryKind, color: string | null) => {
  if (color === null) return null;
  const palette = CATEGORY_COLOR_OPTIONS[kind];
  return palette.includes(color) ? color : palette[0];
};

const sortCategoriesList = (categories: Category[]) =>
  categories
    .slice()
    .sort((a, b) => (a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind.localeCompare(b.kind)));

const getCategoryKindLabel = (kind: CategoryKind) => {
  if (kind === "income") return "Ingreso";
  if (kind === "expense") return "Gasto";
  return "Traspasos";
};

const getAccountTypeLabel = (type: AccountType) => {
  if (type === "checking") return "Cuenta corriente";
  if (type === "savings") return "Ahorro";
  if (type === "cash") return "Efectivo";
  return "Tarjeta de crédito";
};

function parseRuleAmount(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    throw new Error("El importe introducido no es válido");
  }
  return parsed;
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

function formatRuleRange(rule: CategoryRule) {
  const { minAmount, maxAmount } = rule;
  if (minAmount !== null && maxAmount !== null) {
    if (minAmount === maxAmount) {
      return `importe = ${ruleAmountFormatter.format(minAmount)}`;
    }
    return `${ruleAmountFormatter.format(minAmount)} <= importe <= ${ruleAmountFormatter.format(maxAmount)}`;
  }
  if (minAmount !== null) {
    return `importe >= ${ruleAmountFormatter.format(minAmount)}`;
  }
  if (maxAmount !== null) {
    return `importe <= ${ruleAmountFormatter.format(maxAmount)}`;
  }
  return "Sin límite de importe";
}

export default function DashboardSettingsPage() {
  const { token, logout } = useAuth();
  const { refreshBilling, startCheckout } = useBilling();
  const location = useLocation();
  const billingReturnHandledRef = React.useRef<string | null>(null);

  const [accounts, setAccounts] = React.useState<Account[]>([]);
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [categoryRules, setCategoryRules] = React.useState<CategoryRule[]>([]);
  const [cards, setCards] = React.useState<CreditCard[]>([]);

  const [selectedCategoryForRules, setSelectedCategoryForRules] = React.useState<number | null>(null);
  const [ruleForm, setRuleForm] = React.useState({ pattern: "", minAmount: "", maxAmount: "" });
  const [ruleSubmitting, setRuleSubmitting] = React.useState(false);
  const [ruleDeletingId, setRuleDeletingId] = React.useState<number | null>(null);

  const [accountForm, setAccountForm] = React.useState({ name: "", type: "checking" as AccountType });
  const [accountSubmitting, setAccountSubmitting] = React.useState(false);
  const [editingAccountId, setEditingAccountId] = React.useState<number | null>(null);
  const [accountDeletingId, setAccountDeletingId] = React.useState<number | null>(null);
  const [cardForm, setCardForm] = React.useState({
    name: "",
    accountId: "",
  });
  const [cardSubmitting, setCardSubmitting] = React.useState(false);
  const [editingCardId, setEditingCardId] = React.useState<number | null>(null);
  const [cardDeletingId, setCardDeletingId] = React.useState<number | null>(null);
  const [accountPanelOpen, setAccountPanelOpen] = React.useState(false);
  const [cardPanelOpen, setCardPanelOpen] = React.useState(false);

  const [categoryForm, setCategoryForm] = React.useState<{
    name: string;
    kind: CategoryKind;
    color: string | null;
    initialRule: {
      pattern: string;
      minAmount: string;
      maxAmount: string;
    };
  }>({ name: "", kind: "expense", color: getDefaultCategoryColor("expense"), initialRule: emptyInitialRule() });
  const [categorySubmitting, setCategorySubmitting] = React.useState(false);
  const [editingCategoryId, setEditingCategoryId] = React.useState<number | null>(null);
  const [categoryDeletingId, setCategoryDeletingId] = React.useState<number | null>(null);
  const [editCategoryForm, setEditCategoryForm] = React.useState<{
    name: string;
    kind: CategoryKind;
    color: string | null;
  }>({ name: "", kind: "expense", color: null });
  const [categoryDrawer, setCategoryDrawer] = React.useState<{ mode: "edit" | "rules" | "create"; categoryId: number | null } | null>(null);

  const drawerHeadingId = React.useId();
  const drawerSubtitleId = React.useId();


  const [loading, setLoading] = React.useState(true);
  const [globalMessage, setGlobalMessage] = React.useState<string | null>(null);
  const [globalError, setGlobalError] = React.useState<string | null>(null);
  const [planLimitDetails, setPlanLimitDetails] = React.useState<PlanLimitDetails | null>(null);
  const [billingActionLoading, setBillingActionLoading] = React.useState<null | "month" | "year">(null);
  const [activeStep, setActiveStep] = React.useState<SetupStep>("accounts");

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const step = params.get("step");
    if (step === "uploads") {
      setActiveStep("uploads");
    } else if (step === "categories") {
      setActiveStep("categories");
    } else if (step === "accounts") {
      setActiveStep("accounts");
    }
  }, [location.search]);

  const currentYear = new Date().getFullYear();
  const years = React.useMemo(() => Array.from({ length: 6 }, (_, index) => currentYear - index), [currentYear]);

  const [transactionForm, setTransactionForm] = React.useState({
    description: "",
    amount: "",
    transactionDate: defaultTransactionDate(),
    accountId: "",
    categoryId: "",
  });
  const [manualMovementType, setManualMovementType] = React.useState<"account" | "card">("account");
  const [manualCardId, setManualCardId] = React.useState("");
  const [transactionSubmitting, setTransactionSubmitting] = React.useState(false);
  const [manualTransactionModalOpen, setManualTransactionModalOpen] = React.useState(false);

  const [importType, setImportType] = React.useState<"account" | "card">("account");
  const [importAccountId, setImportAccountId] = React.useState("");
  const [importCardId, setImportCardId] = React.useState("");
  const [importHistoryOpen, setImportHistoryOpen] = React.useState(false);
  const [importNotes, setImportNotes] = React.useState("");
  const [autoCreateCategories, setAutoCreateCategories] = React.useState(true);
  const [isImporting, setIsImporting] = React.useState(false);
  const [overwriteYearEnabled, setOverwriteYearEnabled] = React.useState(false);
  const [overwriteYearOption, setOverwriteYearOption] = React.useState(currentYear);
  const [imports, setImports] = React.useState<StatementImport[]>([]);
  const [cardImports, setCardImports] = React.useState<CardImport[]>([]);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const activeAccounts = React.useMemo(() => accounts.filter(account => !account.standby), [accounts]);
  const activeCards = React.useMemo(() => cards.filter(card => !card.standby), [cards]);

  const notifySuccess = React.useCallback((message: string) => {
    setGlobalMessage(message);
    setGlobalError(null);
  }, []);

  const notifyError = React.useCallback((message: string) => {
    setGlobalError(message);
    setGlobalMessage(null);
  }, []);

  const handlePlanLimitError = React.useCallback(
    (error: unknown) => {
      const details = getPlanLimitDetails(error);
      if (!details) return false;
      setPlanLimitDetails(details);
      notifyError(`${getPlanLimitLabel(details.metric)}: ${details.current}/${details.limit}. Pásate a Pro para continuar.`);
      void refreshBilling();
      return true;
    },
    [notifyError, refreshBilling]
  );

  const handleUpgradeMonthly = React.useCallback(async () => {
    setBillingActionLoading("month");
    try {
      await startCheckout("month");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo abrir el checkout mensual"));
    } finally {
      setBillingActionLoading(null);
    }
  }, [startCheckout, notifyError]);

  const handleUpgradeYearly = React.useCallback(async () => {
    setBillingActionLoading("year");
    try {
      await startCheckout("year");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo abrir el checkout anual"));
    } finally {
      setBillingActionLoading(null);
    }
  }, [startCheckout, notifyError]);


  const handleDrawerClose = React.useCallback(() => {
    setCategoryDrawer(null);
    setEditingCategoryId(null);
    setEditCategoryForm({ name: "", kind: "expense", color: null });
    setCategoryForm({ name: "", kind: "expense", color: getDefaultCategoryColor("expense"), initialRule: emptyInitialRule() });
    setSelectedCategoryForRules(null);
    setRuleForm(emptyInitialRule());
    setRuleDeletingId(null);
  }, []);

  const adaptRule = React.useCallback(
    (rule: CategoryRuleApi): CategoryRule => ({
      id: rule.id,
      categoryId: rule.category_id,
      pattern: rule.pattern,
      minAmount: rule.min_amount,
      maxAmount: rule.max_amount,
      createdAt: rule.created_at,
    }),
    []
  );

  const handleRequest = React.useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          logout();
        }
        throw error;
      }
    },
    [logout]
  );

  const loadBaseData = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setGlobalError(null);
    try {
      const [accountsData, categoriesData, rulesData, cardsData, importsData] = await Promise.all([
        handleRequest(apiRequest<Account[]>("/accounts", { token })),
        handleRequest(apiRequest<Category[]>("/categories", { token })),
        handleRequest(apiRequest<CategoryRuleApi[]>("/categories/rules", { token })),
        handleRequest(apiRequest<CreditCard[]>("/cards", { token })),
        handleRequest(apiRequest<StatementImport[]>("/imports", { token })),
      ]);
      setAccounts(accountsData);
      setCategories(categoriesData);
      setCategoryRules(rulesData.map(adaptRule));
      setCards(cardsData);
      setImports(importsData);
      if (categoriesData.length) {
        setSelectedCategoryForRules(prev =>
          prev && categoriesData.some(category => category.id === prev) ? prev : null
        );
      } else {
        setSelectedCategoryForRules(null);
      }
    } catch (error) {
      notifyError("No pudimos cargar la configuración del panel");
    } finally {
      setLoading(false);
    }
  }, [token, handleRequest, adaptRule, notifyError]);

  React.useEffect(() => {
    if (!token) return;
    loadBaseData();
  }, [token, loadBaseData]);

  React.useEffect(() => {
    if (!token) return;
    const params = new URLSearchParams(location.search);
    const billingState = params.get("billing");
    const checkoutSessionId = params.get("session_id");
    if (!billingState) return;
    if (billingReturnHandledRef.current === billingState) return;
    billingReturnHandledRef.current = billingState;

    const cleanUrl = () => {
      const nextParams = new URLSearchParams(location.search);
      nextParams.delete("billing");
      nextParams.delete("session_id");
      const query = nextParams.toString();
      const nextUrl = query ? `${location.pathname}?${query}` : location.pathname;
      window.history.replaceState({}, "", nextUrl);
    };

    if (billingState === "cancel") {
      notifyError("Checkout cancelado. Puedes volver a intentarlo cuando quieras.");
      cleanUrl();
      return;
    }

    if (billingState === "portal-return") {
      void (async () => {
        const wait = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));
        for (let attempt = 0; attempt < 6; attempt += 1) {
          try {
            const result = await apiRequest<SubscriptionSyncResponse>("/billing/subscription/sync", {
              method: "POST",
              token,
            });
            if (result.cancelAtPeriodEnd === true || result.status === "canceled") {
              break;
            }
            if (attempt < 5) await wait(1500);
          } catch (error) {
            console.warn("[billing] No se pudo sincronizar suscripción tras volver del portal", error);
            break;
          }
        }
        await refreshBilling();
        await loadBaseData();
        notifySuccess("Facturación actualizada.");
        cleanUrl();
      })();
      return;
    }

    if (billingState !== "success") {
      cleanUrl();
      return;
    }

    let canceled = false;
    const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

    const syncAfterSuccess = async () => {
      notifySuccess("Pago confirmado. Activando plan Pro...");
      if (checkoutSessionId && checkoutSessionId.startsWith("cs_")) {
        try {
          await apiRequest<CheckoutSessionSyncResponse>("/billing/checkout-session/sync", {
            method: "POST",
            token,
            body: { sessionId: checkoutSessionId },
          });
        } catch (error) {
          console.warn("[billing] No se pudo sincronizar checkout con session_id", error);
        }
      } else if (checkoutSessionId) {
        console.warn("[billing] session_id inválido en retorno de checkout", checkoutSessionId);
      }

      let activated = false;
      for (let attempt = 0; attempt < 8; attempt += 1) {
        if (canceled) return;
        const billing = await apiRequest<BillingMeResponse>("/billing/me", { token });
        await refreshBilling();
        await loadBaseData();
        if (billing.plan.code === "pro" || billing.isBypassed) {
          activated = true;
          break;
        }
        await wait(1500);
      }

      if (canceled) return;
      if (activated) {
        notifySuccess("Plan Pro activado. Ya tienes todas las funcionalidades desbloqueadas.");
      } else {
        notifySuccess("Pago confirmado. En unos segundos se activarán todas las funcionalidades Pro.");
      }
      cleanUrl();
    };

    void syncAfterSuccess().catch(error => {
      if (canceled) return;
      notifyError(extractErrorMessage(error, "No pudimos verificar la activación del plan Pro todavía."));
      cleanUrl();
    });

    return () => {
      canceled = true;
    };
  }, [token, location.pathname, location.search, loadBaseData, notifyError, notifySuccess, refreshBilling]);

  React.useEffect(() => {
    if (!activeAccounts.length) return;
    setCardForm(prev => (prev.accountId ? prev : { ...prev, accountId: String(activeAccounts[0].id) }));
    setImportAccountId(prev => (prev ? prev : String(activeAccounts[0].id)));
    setTransactionForm(prev => (prev.accountId ? prev : { ...prev, accountId: String(activeAccounts[0].id) }));
  }, [activeAccounts]);

  React.useEffect(() => {
    if (!activeCards.length) return;
    setImportCardId(prev => (prev ? prev : String(activeCards[0].id)));
    setManualCardId(prev => (prev ? prev : String(activeCards[0].id)));
  }, [activeCards]);

  React.useEffect(() => {
    setRuleForm({ pattern: "", minAmount: "", maxAmount: "" });
  }, [selectedCategoryForRules]);

  React.useEffect(() => {
    if (!overwriteYearEnabled) {
      setOverwriteYearOption(currentYear);
    }
  }, [overwriteYearEnabled, currentYear]);

  const rulesByCategory = React.useMemo(() => {
    const map = new Map<number | "uncategorized", number>();
    for (const rule of categoryRules) {
      const key = (rule.categoryId ?? "uncategorized") as number | "uncategorized";
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [categoryRules]);

  const sortedCategories = React.useMemo(
    () => [...categories].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })),
    [categories]
  );

  const rulesForSelectedCategory = React.useMemo(
    () =>
      selectedCategoryForRules === null
        ? []
        : categoryRules
            .filter(rule => rule.categoryId === selectedCategoryForRules)
            .slice()
            .sort((a, b) => a.pattern.localeCompare(b.pattern)),
    [categoryRules, selectedCategoryForRules]
  );

  const drawerCategory = React.useMemo(
    () =>
      categoryDrawer && categoryDrawer.mode !== "create"
        ? categories.find(category => category.id === categoryDrawer.categoryId) ?? null
        : null,
    [categoryDrawer, categories]
  );

  React.useEffect(() => {
    if (!categoryDrawer) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleDrawerClose();
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [categoryDrawer, handleDrawerClose]);

  React.useEffect(() => {
    if (categoryDrawer && categoryDrawer.mode !== "create" && !drawerCategory) {
      handleDrawerClose();
    }
  }, [categoryDrawer, drawerCategory, handleDrawerClose]);

  const availableCategoryColors = React.useMemo(() => {
    const palette = CATEGORY_COLOR_OPTIONS[categoryForm.kind];
    if (categoryForm.color && !palette.includes(categoryForm.color)) {
      return [...palette, categoryForm.color];
    }
    return palette;
  }, [categoryForm.kind, categoryForm.color]);

  const availableEditCategoryColors = React.useMemo(() => {
    const palette = CATEGORY_COLOR_OPTIONS[editCategoryForm.kind];
    if (editCategoryForm.color && !palette.includes(editCategoryForm.color)) {
      return [...palette, editCategoryForm.color];
    }
    return palette;
  }, [editCategoryForm.kind, editCategoryForm.color]);

  const accountsById = React.useMemo(() => {
    const map = new Map<number, Account>();
    accounts.forEach(account => map.set(account.id, account));
    return map;
  }, [accounts]);

  const categoriesById = React.useMemo(() => {
    const map = new Map<number, Category>();
    categories.forEach(category => map.set(category.id, category));
    return map;
  }, [categories]);

  const protectedCardCategoryIds = React.useMemo(() => {
    const ids = new Set<number>();
    cards.forEach(card => {
      if (card.billing_category_id) {
        ids.add(card.billing_category_id);
      }
    });
    return ids;
  }, [cards]);

  const loadCardImports = React.useCallback(async () => {
    if (!token || !importCardId) {
      setCardImports([]);
      return;
    }
    try {
      const rows = await handleRequest(apiRequest<CardImport[]>(`/card-imports?cardId=${importCardId}`, { token }));
      setCardImports(rows);
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos cargar las importaciones de tarjeta"));
    }
  }, [token, importCardId, handleRequest, notifyError]);

  React.useEffect(() => {
    if (!token) return;
    loadCardImports();
  }, [token, loadCardImports]);

  const handleAccountSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!accountForm.name.trim()) {
      notifyError("El nombre de la cuenta es obligatorio");
      return;
    }
    setAccountSubmitting(true);
    try {
      if (editingAccountId) {
        const updated = await handleRequest(
          apiRequest<Account>(`/accounts/${editingAccountId}`, {
            method: "PUT",
            token,
            body: { ...accountForm, currency: "EUR" },
          })
        );
        setAccounts(prev => prev.map(account => (account.id === updated.id ? updated : account)));
        notifySuccess(`Cuenta "${updated.name}" actualizada correctamente`);
      } else {
        const created = await handleRequest(
          apiRequest<Account>("/accounts", {
            method: "POST",
            token,
            body: { ...accountForm, currency: "EUR" },
          })
        );
        setAccounts(prev => [...prev, created]);
        notifySuccess(`Cuenta "${created.name}" creada correctamente`);
      }
      setEditingAccountId(null);
      setAccountForm({ name: "", type: "checking" });
      setAccountPanelOpen(false);
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(extractErrorMessage(error, "No se pudo guardar la cuenta"));
    } finally {
      setAccountSubmitting(false);
    }
  };

  const handleStartAccountEdit = (account: Account) => {
    setEditingAccountId(account.id);
    setAccountForm({ name: account.name, type: account.type });
    setAccountPanelOpen(true);
  };

  const handleOpenCreateAccount = () => {
    setEditingAccountId(null);
    setAccountForm({ name: "", type: "checking" });
    setAccountPanelOpen(true);
  };

  const handleCloseAccountPanel = () => {
    setAccountPanelOpen(false);
    setEditingAccountId(null);
  };

  const handleAccountDelete = async (account: Account) => {
    const confirmed = window.confirm(`¿Quieres eliminar la cuenta "${account.name}"?`);
    if (!confirmed) return;
    setAccountDeletingId(account.id);
    try {
      await handleRequest(
        apiRequest<void>(`/accounts/${account.id}`, {
          method: "DELETE",
          token,
        })
      );
      setAccounts(prev => prev.filter(item => item.id !== account.id));
      setCards(prev => prev.filter(card => card.account_id !== account.id));
      notifySuccess("Cuenta eliminada");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No se pudo eliminar la cuenta"));
    } finally {
      setAccountDeletingId(null);
    }
  };

  const handleCardSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!cardForm.name.trim() || !cardForm.accountId) {
      notifyError("Completa el nombre y la cuenta de cargo");
      return;
    }
    setCardSubmitting(true);
    try {
      const payload = {
        name: cardForm.name.trim(),
        accountId: Number(cardForm.accountId),
        currency: accountsById.get(Number(cardForm.accountId))?.currency ?? "EUR",
      };
      if (editingCardId) {
        const updated = await handleRequest(
          apiRequest<CreditCard>(`/cards/${editingCardId}`, {
            method: "PUT",
            token,
            body: payload,
          })
        );
        setCards(prev => prev.map(card => (card.id === updated.id ? updated : card)));
        notifySuccess("Tarjeta actualizada");
      } else {
        const newCard = await handleRequest(
          apiRequest<CreditCard>("/cards", {
            method: "POST",
            token,
            body: payload,
          })
        );
        setCards(prev => [newCard, ...prev]);
        notifySuccess("Tarjeta creada");
      }
      setEditingCardId(null);
      setCardForm(prev => ({
        name: "",
        accountId: prev.accountId || (activeAccounts[0] ? String(activeAccounts[0].id) : ""),
      }));
      setCardPanelOpen(false);
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(extractErrorMessage(error, "No pudimos guardar la tarjeta"));
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleStartCardEdit = (card: CreditCard) => {
    setEditingCardId(card.id);
    setCardForm({
      name: card.name,
      accountId: String(card.account_id),
    });
    setCardPanelOpen(true);
  };

  const handleOpenCreateCard = () => {
    setEditingCardId(null);
    setCardForm(prev => ({
      name: "",
      accountId: prev.accountId || (activeAccounts[0] ? String(activeAccounts[0].id) : ""),
    }));
    setCardPanelOpen(true);
  };

  const handleCloseCardPanel = () => {
    setCardPanelOpen(false);
    setEditingCardId(null);
  };

  const handleCardDelete = async (card: CreditCard) => {
    const confirmed = window.confirm(`¿Quieres eliminar la tarjeta "${card.name}"?`);
    if (!confirmed) return;
    setCardDeletingId(card.id);
    try {
      await handleRequest(
        apiRequest(`/cards/${card.id}`, {
          method: "DELETE",
          token,
        })
      );
      setCards(prev => prev.filter(item => item.id !== card.id));
      notifySuccess("Tarjeta eliminada");
    } catch (error) {
      notifyError(extractErrorMessage(error, "No pudimos eliminar la tarjeta"));
    } finally {
      setCardDeletingId(null);
    }
  };

  const handleStartCategoryEdit = (category: Category) => {
    if (protectedCardCategoryIds.has(category.id)) {
      notifyError("La categoría asociada a una tarjeta se gestiona automáticamente y no se puede editar.");
      return;
    }
    setEditingCategoryId(category.id);
    setEditCategoryForm({
      name: category.name,
      kind: category.kind,
      color: category.color ?? null,
    });
    setCategoryDrawer({ mode: "edit", categoryId: category.id });
  };

  const handleCategoryDelete = async (category: Category) => {
    if (protectedCardCategoryIds.has(category.id)) {
      notifyError("La categoría asociada a una tarjeta se gestiona automáticamente y no se puede eliminar.");
      return;
    }
    const confirmed = window.confirm(
      `¿Quieres eliminar la categoría "${category.name}"? Los movimientos vinculados pasarán a "Sin categoría".`
    );
    if (!confirmed) return;
    setCategoryDeletingId(category.id);
    try {
      await handleRequest(
        apiRequest<void>(`/categories/${category.id}`, {
          method: "DELETE",
          token,
        })
      );
      setCategories(prev => {
        const next = prev.filter(item => item.id !== category.id);
        if (categoryDrawer && categoryDrawer.categoryId === category.id) {
          setCategoryDrawer(null);
        }
        setSelectedCategoryForRules(current => {
          if (current === category.id) {
            return null;
          }
          return current;
        });
        return sortCategoriesList(next);
      });
      setCategoryRules(prev => prev.filter(rule => rule.categoryId !== category.id));
      if (editingCategoryId === category.id) {
        setEditingCategoryId(null);
        setEditCategoryForm({ name: "", kind: "expense", color: null });
      }
      setRuleForm(emptyInitialRule());
      notifySuccess(`Categoría "${category.name}" eliminada`);
    } catch (error) {
      notifyError("No se pudo eliminar la categoría");
    } finally {
      setCategoryDeletingId(null);
    }
  };

  const handleOpenRulesDrawer = (category: Category) => {
    setSelectedCategoryForRules(category.id);
    setRuleForm(emptyInitialRule());
    setRuleDeletingId(null);
    setCategoryDrawer({ mode: "rules", categoryId: category.id });
  };

  const handleOpenCreateDrawer = () => {
    setEditingCategoryId(null);
    setSelectedCategoryForRules(null);
    setCategoryForm({ name: "", kind: "expense", color: getDefaultCategoryColor("expense"), initialRule: emptyInitialRule() });
    setCategoryDrawer({ mode: "create", categoryId: null });
  };

  const handleCategorySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!categoryForm.name.trim()) {
      notifyError("El nombre de la categoría es obligatorio");
      return;
    }
    const nextKindAfterSubmit = categoryForm.kind;
    const nextColorAfterSubmit = categoryForm.color;
    const initialRulePattern = categoryForm.initialRule.pattern.trim();
    let initialRulePayload: { pattern: string; minAmount: number | null; maxAmount: number | null } | null = null;
    if (initialRulePattern) {
      try {
        initialRulePayload = {
          pattern: initialRulePattern,
          minAmount: parseRuleAmount(categoryForm.initialRule.minAmount),
          maxAmount: parseRuleAmount(categoryForm.initialRule.maxAmount),
        };
      } catch (error) {
        notifyError(error instanceof Error ? error.message : "El importe de la regla inicial es inválido");
        return;
      }
    }
    const payload = {
      name: categoryForm.name.trim(),
      kind: categoryForm.kind,
      color: categoryForm.color,
    };
    setCategorySubmitting(true);
    try {
      const created = await handleRequest(
        apiRequest<Category>("/categories", {
          method: "POST",
          token,
          body: payload,
        })
      );
      setCategories(prev => sortCategoriesList([...prev, created]));
      setSelectedCategoryForRules(created.id);
      let showSuccessMessage = true;
      setCategoryForm({
        name: "",
        kind: nextKindAfterSubmit,
        color:
          nextColorAfterSubmit === null
            ? null
            : normalizeColorForKind(nextKindAfterSubmit, nextColorAfterSubmit),
        initialRule: emptyInitialRule(),
      });
      if (initialRulePayload) {
        try {
          const createdRule = await handleRequest(
            apiRequest<CategoryRuleApi>(`/categories/${created.id}/rules`, {
              method: "POST",
              token,
              body: initialRulePayload,
            })
          );
          setCategoryRules(prev => [...prev, adaptRule(createdRule)]);
        } catch (error) {
          if (handlePlanLimitError(error)) {
            showSuccessMessage = false;
          } else {
            notifyError(
              error instanceof Error
                ? error.message
                : "Categoría creada pero no se pudo crear la regla inicial"
            );
            showSuccessMessage = false;
          }
        }
      }
      if (showSuccessMessage) {
        notifySuccess(`Categoría "${created.name}" creada`);
      }
      if (categoryDrawer?.mode === "create") {
        handleDrawerClose();
      }
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError("No se pudo crear la categoría");
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleEditCategorySubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editingCategoryId) return;
    if (!editCategoryForm.name.trim()) {
      notifyError("El nombre de la categoría es obligatorio");
      return;
    }
    const payload = {
      name: editCategoryForm.name.trim(),
      kind: editCategoryForm.kind,
      color: editCategoryForm.color,
    };
    setCategorySubmitting(true);
    try {
      const updated = await handleRequest(
        apiRequest<Category>(`/categories/${editingCategoryId}`, {
          method: "PUT",
          token,
          body: payload,
        })
      );
      setCategories(prev => sortCategoriesList(prev.map(category => (category.id === updated.id ? updated : category))));
      notifySuccess(`Categoría "${updated.name}" actualizada`);
      handleDrawerClose();
    } catch (error) {
      notifyError("No se pudo actualizar la categoría");
    } finally {
      setCategorySubmitting(false);
    }
  };

  const handleRuleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedCategoryForRules) {
      notifyError("Selecciona una categoría para añadir reglas automáticas");
      return;
    }
    if (!ruleForm.pattern.trim()) {
      notifyError("El patrón de la regla es obligatorio");
      return;
    }
    let minAmount: number | null;
    let maxAmount: number | null;
    try {
      minAmount = parseRuleAmount(ruleForm.minAmount);
      maxAmount = parseRuleAmount(ruleForm.maxAmount);
    } catch (error) {
      notifyError(error instanceof Error ? error.message : "El importe introducido no es válido");
      return;
    }

    setRuleSubmitting(true);
    try {
      const payload = {
        pattern: ruleForm.pattern.trim(),
        minAmount,
        maxAmount,
      };
      const created = await handleRequest(
        apiRequest<CategoryRuleApi>(`/categories/${selectedCategoryForRules}/rules`, {
          method: "POST",
          token,
          body: payload,
        })
      );
      const mapped = adaptRule(created);
      setCategoryRules(prev => [...prev, mapped]);
      setRuleForm({ pattern: "", minAmount: "", maxAmount: "" });
      notifySuccess("Regla guardada correctamente");
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(error instanceof Error ? error.message : "No se pudo crear la regla");
    } finally {
      setRuleSubmitting(false);
    }
  };

  const handleRuleDelete = async (ruleId: number) => {
    if (!window.confirm("¿Quieres eliminar esta regla automática?")) return;
    setRuleDeletingId(ruleId);
    try {
      await handleRequest(
        apiRequest<void>(`/categories/rules/${ruleId}`, {
          method: "DELETE",
          token,
        })
      );
      setCategoryRules(prev => prev.filter(rule => rule.id !== ruleId));
      notifySuccess("Regla eliminada");
    } catch (error) {
      notifyError("No se pudo eliminar la regla");
    } finally {
      setRuleDeletingId(null);
    }
  };

  const setupSteps = React.useMemo(
    () =>
      [
        { id: "accounts" as SetupStep, title: "Mis cuentas y tarjetas", number: 1 },
        { id: "categories" as SetupStep, title: "Mis categorías", number: 2 },
        { id: "uploads" as SetupStep, title: "Subir datos", number: 3 },
      ] as const,
    []
  );

  const currentStepIndex = React.useMemo(
    () => setupSteps.findIndex(step => step.id === activeStep),
    [setupSteps, activeStep]
  );

  const goPrevStep = React.useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) setActiveStep(setupSteps[prevIndex].id);
  }, [currentStepIndex, setupSteps]);

  const goNextStep = React.useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < setupSteps.length) setActiveStep(setupSteps[nextIndex].id);
  }, [currentStepIndex, setupSteps]);

  const handleTransactionSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const amount = Number(transactionForm.amount.replace(",", "."));
    if (Number.isNaN(amount)) {
      notifyError("Introduce un importe válido");
      return;
    }
    setTransactionSubmitting(true);
    try {
      if (manualMovementType === "account") {
        if (!transactionForm.accountId) {
          notifyError("Selecciona una cuenta para el movimiento");
          return;
        }
        await handleRequest(
          apiRequest("/transactions", {
            method: "POST",
            token,
            body: {
              accountId: Number(transactionForm.accountId),
              categoryId: transactionForm.categoryId ? Number(transactionForm.categoryId) : null,
              description: transactionForm.description || null,
              amount,
              transactionDate: transactionForm.transactionDate,
            },
          })
        );
      } else {
        if (!manualCardId) {
          notifyError("Selecciona una tarjeta para el movimiento");
          return;
        }
        await handleRequest(
          apiRequest("/card-transactions", {
            method: "POST",
            token,
            body: {
              cardId: Number(manualCardId),
              description: transactionForm.description || null,
              amount,
              transactionDate: transactionForm.transactionDate,
            },
          })
        );
      }
      setTransactionForm(prev => ({
        ...prev,
        description: "",
        amount: "",
        transactionDate: defaultTransactionDate(),
        categoryId: "",
      }));
      notifySuccess(manualMovementType === "account" ? "Movimiento de cuenta registrado con éxito" : "Movimiento de tarjeta registrado con éxito");
      setManualTransactionModalOpen(false);
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(extractErrorMessage(error, "No se pudo registrar el movimiento"));
    } finally {
      setTransactionSubmitting(false);
    }
  };

  const handleImportSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      notifyError(importType === "account" ? "Selecciona un archivo CSV, XLSX o PDF para importar" : "Selecciona un archivo CSV o XLSX para importar");
      return;
    }
    if (importType === "account" && !importAccountId) {
      notifyError("Selecciona la cuenta asociada al extracto");
      return;
    }
    if (importType === "card" && !importCardId) {
      notifyError("Selecciona la tarjeta asociada al extracto");
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (importNotes.trim()) formData.append("notes", importNotes.trim());
      if (overwriteYearEnabled) formData.append("overwriteYear", String(overwriteYearOption));

      if (importType === "account") {
        formData.append("accountId", importAccountId);

        formData.append("autoCreateCategories", autoCreateCategories ? "true" : "false");
        const result = await handleRequest(
          apiRequest<ImportResponse>("/imports", {
            method: "POST",
            token,
            body: formData,
          })
        );
        if (result.overwrite) {
          await loadBaseData();
          setOverwriteYearEnabled(false);
        } else {
          setImports(prev => [result.statement, ...prev]);
        }
      } else {
        formData.append("cardId", importCardId);
        formData.append("autoCreateCategories", autoCreateCategories ? "true" : "false");
        const result = await handleRequest(
          apiRequest<CardImportResponse>("/card-imports", {
            method: "POST",
            token,
            body: formData,
          })
        );
        if (result.overwrite) {
          setOverwriteYearEnabled(false);
        } else {
          setCardImports(prev => [result.statement, ...prev]);
        }
        await loadCardImports();
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
      setImportNotes("");
      notifySuccess("Importación completada correctamente");
    } catch (error) {
      if (handlePlanLimitError(error)) return;
      notifyError(extractErrorMessage(error, "No se pudo importar el archivo"));
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <DashboardLayout active="settings">
      <div className="settings-shell">
      <div className="flex flex-col">
        <FloatingToast
          message={globalError || globalMessage}
          tone={globalError ? "error" : "success"}
          onClear={() => {
            setGlobalError(null);
            setGlobalMessage(null);
          }}
        />

        {loading ? (
          <div className="setup-content">
            <div className="panel">
              <div className="loader">
                <div className="spinner" />
                Cargando configuración...
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-white border-b border-gray-200 px-4 py-4 lg:px-8 lg:py-6">
              <h1 className="text-2xl font-bold text-gray-900">Configuración inicial</h1>
              <p className="text-sm text-gray-600 mt-1">Configura tus cuentas, categorías y carga movimientos</p>
            </div>

            <div className="bg-white border-b border-gray-200 px-4 py-4 lg:px-8">
              <div className="flex items-center justify-between max-w-3xl mx-auto">
                {setupSteps.map((step, index) => {
                  const isActive = activeStep === step.id;
                  const isCompleted = index < currentStepIndex;
                  const stepTitle = step.id === "categories" ? `Mis categorías (${categories.length})` : step.title;
                  return (
                    <div key={step.id} className="flex items-center flex-1">
                      <button
                        type="button"
                        onClick={() => setActiveStep(step.id)}
                        className={`flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-left w-full transition-colors ${
                          isActive
                            ? "text-blue-600"
                            : isCompleted
                              ? "text-green-600 hover:text-green-700"
                              : "text-gray-400 hover:text-gray-600"
                        }`}
                      >
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : isCompleted
                                ? "bg-green-600 text-white"
                                : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {isCompleted ? "✓" : step.number}
                        </div>
                        <div className="text-center sm:text-left">
                          <div
                            className={`text-xs font-medium ${
                              isActive ? "text-blue-600" : isCompleted ? "text-green-600" : "text-gray-500"
                            }`}
                          >
                            Paso {step.number}
                          </div>
                          <div className="font-semibold text-xs sm:text-sm">{stepTitle}</div>
                        </div>
                      </button>
                      {index < setupSteps.length - 1 && (
                        <IconChevronRight
                          className={`w-5 h-5 mx-2 flex-shrink-0 hidden sm:block ${isCompleted ? "text-green-600" : "text-gray-300"}`}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="px-4 py-6 lg:px-8 pb-40">
            {activeStep === "accounts" ? (
              <div className="settings-section setup-step-content space-y-6 max-w-5xl mx-auto">
                <section>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Cuentas bancarias ({accounts.length})</h2>
                      <p className="text-sm text-gray-600 mt-1">Gestiona de forma visual las cuentas que se usarán en la operativa.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenCreateAccount}
                      className="btn btn-primary inline-flex items-center gap-2"
                    >
                      <IconPlus className="w-4 h-4" />
                      Nueva cuenta
                    </button>
                  </div>
                  {accounts.some(account => account.standby) ? (
                    <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Algunas cuentas están en <strong>standby</strong> por superar el límite del plan Free. Sus movimientos no se
                      incluyen en totalizaciones ni análisis.
                    </div>
                  ) : null}

                  {accounts.length ? (
                    <div className="grid gap-4 md:grid-cols-2 mt-4">
                      {accounts.map(account => {
                        const isDeleting = accountDeletingId === account.id;
                        const isStandby = Boolean(account.standby);
                        return (
                        <article
                          key={account.id}
                          className={`bg-white rounded-lg border p-4 ${isStandby ? "border-amber-300 bg-amber-50/50" : "border-gray-200"}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconWallet className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900">{account.name}</h3>
                              <p className="text-sm text-gray-500">{getAccountTypeLabel(account.type)} · {account.currency}</p>
                              {isStandby ? <p className="text-xs font-semibold text-amber-700 mt-1">Standby (plan Free)</p> : null}
                              <p className="text-sm text-gray-600 mt-2">
                                {cards.filter(card => card.account_id === account.id).length} tarjetas asociadas
                              </p>
                            </div>
                          </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                                onClick={() => handleStartAccountEdit(account)}
                                disabled={isStandby}
                                aria-label="Editar cuenta"
                              >
                                <IconEdit2 className="w-4 h-4" />
                              </button>
                              <button
                                type="button"
                                className="p-1.5 hover:bg-gray-100 rounded text-red-600 disabled:opacity-60"
                                onClick={() => handleAccountDelete(account)}
                                disabled={isDeleting || isStandby}
                                aria-label={isDeleting ? "Eliminando cuenta" : "Eliminar cuenta"}
                              >
                                <IconTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <IconWallet className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-3">No hay cuentas configuradas</p>
                      <button
                        type="button"
                        onClick={handleOpenCreateAccount}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Añadir primera cuenta
                      </button>
                    </div>
                  )}
                </section>

                <section>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Tarjetas de crédito ({cards.length})</h2>
                      <p className="text-sm text-gray-600 mt-1">Vincula cada tarjeta a su cuenta. La categoría de cargo se crea automáticamente.</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenCreateCard}
                      disabled={activeAccounts.length === 0}
                      className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <IconPlus className="w-4 h-4" />
                      Nueva tarjeta
                    </button>
                  </div>

                  {activeAccounts.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-sm text-amber-900">
                        Necesitas al menos una cuenta activa para poder añadir tarjetas.
                      </p>
                    </div>
                  ) : cards.length ? (
                    <div className="grid gap-4 md:grid-cols-2 mt-4">
                      {cards.map(card => {
                        const account = accountsById.get(card.account_id);
                        const billingCategory = card.billing_category_id ? categoriesById.get(card.billing_category_id) : null;
                        const isDeleting = cardDeletingId === card.id;
                        const isStandby = Boolean(card.standby);
                        return (
                          <article
                            key={card.id}
                            className={`bg-white rounded-lg border p-4 ${isStandby ? "border-amber-300 bg-amber-50/50" : "border-gray-200"}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <IconCreditCard className="w-5 h-5 text-indigo-600" />
                                </div>
                                <div className="min-w-0">
                                  <h3 className="font-semibold text-gray-900">{card.name}</h3>
                                  <p className="text-sm text-gray-600 mt-1">Cuenta: {account?.name ?? "Sin cuenta"}</p>
                                  <p className="text-sm text-gray-600">Categoría automática: {billingCategory?.name ?? "Se creará automáticamente"}</p>
                                  {isStandby ? <p className="text-xs font-semibold text-amber-700 mt-1">Standby (plan Free)</p> : null}
                                  <p className="text-sm text-gray-500 mt-1">Moneda: {card.currency}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <button
                                  type="button"
                                  className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                                  onClick={() => handleStartCardEdit(card)}
                                  disabled={isStandby}
                                  aria-label="Editar tarjeta"
                                >
                                  <IconEdit2 className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  className="p-1.5 hover:bg-gray-100 rounded text-red-600 disabled:opacity-60"
                                  onClick={() => handleCardDelete(card)}
                                  disabled={isDeleting || isStandby}
                                  aria-label={isDeleting ? "Eliminando tarjeta" : "Eliminar tarjeta"}
                                >
                                  <IconTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <IconCreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-gray-600 mb-3">No hay tarjetas configuradas</p>
                      <button
                        type="button"
                        onClick={handleOpenCreateCard}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Añadir primera tarjeta
                      </button>
                    </div>
                  )}
                </section>
              </div>
            ) : activeStep === "categories" ? (
              <div className="settings-section setup-step-content max-w-5xl mx-auto space-y-8">
                <section className="space-y-4">
                  <div className="setup-step-title">
                    <div>
                      <p>Clasifica tus movimientos con categorías y reglas automáticas por patrón e importe.</p>
                    </div>
                    <button type="button" className="btn btn-primary inline-flex items-center gap-2" onClick={handleOpenCreateDrawer}>
                      <IconPlus className="w-4 h-4" />
                      Nueva categoría
                    </button>
                  </div>
                  {sortedCategories.length ? <div className="setup-categories-grid">
                    {sortedCategories.map(category => {
                      const rulesCount = rulesByCategory.get(category.id) ?? 0;
                      const isDeleting = categoryDeletingId === category.id;
                      const isEditing = editingCategoryId === category.id;
                      const isProtected = protectedCardCategoryIds.has(category.id);
                      return (
                        <article
                          key={category.id}
                          className={`setup-category-card${isProtected ? " setup-category-card--protected" : ""}`}
                        >
                          <div className="setup-category-head">
                            <div className="setup-category-label">
                              <span
                                aria-hidden="true"
                                style={{
                                  width: "2.25rem",
                                  height: "2.25rem",
                                  borderRadius: "0.65rem",
                                  backgroundColor: `${category.color ?? "#3b82f6"}20`,
                                  color: category.color ?? "#3b82f6",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <IconTag className="w-4 h-4" />
                              </span>
                              <div className="setup-category-meta">
                                <strong>{category.name}</strong>
                              </div>
                            </div>
                            <div className="setup-category-actions-col">
                              {!isProtected ? (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    type="button"
                                    className="p-1.5 hover:bg-gray-100 rounded text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={() => handleStartCategoryEdit(category)}
                                    disabled={isDeleting}
                                    aria-label={isEditing ? "Editando categoría" : "Editar categoría"}
                                    title="Editar"
                                  >
                                    <IconEdit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    type="button"
                                    className="p-1.5 hover:bg-gray-100 rounded text-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                                    onClick={() => handleCategoryDelete(category)}
                                    disabled={isDeleting}
                                    aria-label={isDeleting ? "Eliminando categoría" : "Eliminar categoría"}
                                    title="Eliminar"
                                  >
                                    <IconTrash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : null}
                              <span className={`setup-category-kind setup-category-kind--${category.kind}`}>
                                {getCategoryKindLabel(category.kind)}
                              </span>
                              {isProtected ? <span className="setup-category-kind setup-category-kind--auto">Automática tarjeta</span> : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="setup-category-rules-button"
                            onClick={() => handleOpenRulesDrawer(category)}
                            disabled={isDeleting}
                            aria-label="Gestionar reglas"
                            title="Gestionar reglas"
                          >
                            <IconZap className="w-4 h-4" />
                            <span>Gestionar reglas</span>
                            <span className="setup-category-rules-pill">
                              {rulesCount}
                            </span>
                          </button>
                        </article>
                      );
                    })}
                  </div> : (
                    <div className="empty-state">Crea tus categorías para comenzar a clasificar los movimientos.</div>
                  )}
                </section>
              </div>
            ) : (
              <div className="settings-section setup-step-content max-w-5xl mx-auto space-y-6">
                <section className="panel" id="import-section">
                  <div className="panel-header">
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.2rem" }}>
                      <h3 className="panel-title">Subir datos</h3>
                      <p className="panel-subtitle">Importa extractos y registra movimientos manuales</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        className="btn btn-secondary inline-flex items-center gap-2"
                        onClick={() => setManualTransactionModalOpen(true)}
                      >
                        <IconPlus className="w-4 h-4" />
                        Registrar movimiento manual
                      </button>
                      <span className="chip">Paso 3</span>
                    </div>
                  </div>

                  <div className="settings-subnav">
                    <button
                      type="button"
                      className={`settings-subnav-button${importType === "account" ? " is-active" : ""}`}
                      onClick={() => setImportType("account")}
                    >
                      Extracto de cuenta
                    </button>
                    <button
                      type="button"
                      className={`settings-subnav-button${importType === "card" ? " is-active" : ""}`}
                      onClick={() => setImportType("card")}
                    >
                      Extracto de tarjeta
                    </button>
                  </div>

                  <form className="form-grid form-grid-2" onSubmit={handleImportSubmit}>
                    {importType === "account" ? (
                      <label style={{ fontWeight: 600, color: "#334155" }}>
                        Cuenta
                        <select
                          className="input"
                          value={importAccountId}
                          onChange={event => setImportAccountId(event.target.value)}
                          required
                        >
                          <option value="">Selecciona cuenta</option>
                          {activeAccounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : (
                      <label style={{ fontWeight: 600, color: "#334155" }}>
                        Tarjeta
                        <select
                          className="input"
                          value={importCardId}
                          onChange={event => setImportCardId(event.target.value)}
                          required
                        >
                          <option value="">Selecciona tarjeta</option>
                          {activeCards.map(card => (
                            <option key={card.id} value={card.id}>
                              {card.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    <label style={{ fontWeight: 600, color: "#334155" }}>
                      {importType === "account" ? "Archivo CSV, XLSX o PDF" : "Archivo CSV o XLSX"}
                      <input
                        className="input"
                        type="file"
                        accept={importType === "account" ? ".csv,.xlsx,.xls,.pdf" : ".csv,.xlsx,.xls"}
                        ref={fileInputRef}
                        required
                      />
                    </label>

                    <div
                      style={{
                        gridColumn: "1 / span 2",
                        padding: "0.9rem 1rem",
                        borderRadius: "0.85rem",
                        border: "1px solid rgba(37, 99, 235, 0.18)",
                        background: "rgba(239, 246, 255, 0.85)",
                        display: "grid",
                        gap: "0.35rem",
                      }}
                    >
                      <p style={{ margin: 0, fontWeight: 700, color: "#1e3a8a" }}>
                        Soporte oficial de importacion
                      </p>
                      {importType === "account" ? (
                        <>
                          <p style={{ margin: 0, color: "#334155", fontSize: "0.95rem" }}>
                            Cuentas corrientes validadas actualmente: <strong>Bankinter Excel</strong>, <strong>Santander Excel</strong> y <strong>BBVA PDF</strong> (layout validado).
                          </p>
                          <p style={{ margin: 0, color: "#475569", fontSize: "0.92rem" }}>
                            Tambien intentamos detectar automaticamente variantes compatibles de CSV y Excel. En PDF solo se admite por ahora el layout validado de BBVA. Si tu banco no aparece o el archivo falla, ese layout todavia no esta validado oficialmente.
                          </p>
                        </>
                      ) : (
                        <p style={{ margin: 0, color: "#475569", fontSize: "0.92rem" }}>
                          La mejora multibanco actual esta centrada en <strong>cuentas corrientes</strong>. La importacion de tarjetas mantiene el comportamiento anterior y puede requerir layouts mas concretos.
                        </p>
                      )}
                    </div>

                    <label style={{ gridColumn: "1 / span 2", fontWeight: 600, color: "#334155" }}>
                      Notas (opcional)
                      <input
                        className="input"
                        value={importNotes}
                        onChange={event => setImportNotes(event.target.value)}
                        placeholder="Ej: Extracto enero"
                      />
                    </label>

                    <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "#334155" }}>
                      <input
                        type="checkbox"
                        checked={autoCreateCategories}
                        onChange={event => setAutoCreateCategories(event.target.checked)}
                      />
                      Crear categorías automáticamente cuando no existan
                    </label>

                    <div
                      style={{
                        gridColumn: "1 / span 2",
                        display: "grid",
                        gap: "0.75rem",
                        padding: "0.9rem 1rem",
                        borderRadius: "0.85rem",
                        border: "1px solid rgba(245, 158, 11, 0.35)",
                        background: overwriteYearEnabled ? "rgba(251, 191, 36, 0.18)" : "rgba(255, 247, 237, 0.8)",
                      }}
                    >
                      <label style={{ display: "flex", alignItems: "center", gap: "0.75rem", color: "#b45309", fontWeight: 600 }}>
                        <input
                          type="checkbox"
                          checked={overwriteYearEnabled}
                          onChange={event => setOverwriteYearEnabled(event.target.checked)}
                        />
                        Borrar todos los movimientos del año en curso antes de cargar el nuevo archivo
                      </label>
                      {overwriteYearEnabled && (
                        <select
                          className="input"
                          style={{ maxWidth: "200px" }}
                          value={overwriteYearOption}
                          onChange={event => setOverwriteYearOption(Number(event.target.value))}
                        >
                          {years.map(year => (
                            <option key={`overwrite-${year}`} value={year}>
                              {year}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div style={{ gridColumn: "1 / span 2", display: "flex", justifyContent: "flex-end" }}>
                      <button type="submit" className="btn btn-primary" disabled={isImporting}>
                        {isImporting ? "Importando..." : "Importar archivo"}
                      </button>
                    </div>
                  </form>

                  <hr className="panel-divider" />
                  <button
                    type="button"
                    onClick={() => setImportHistoryOpen(open => !open)}
                    className="settings-history-toggle"
                    aria-expanded={importHistoryOpen}
                    aria-controls="imports-history-panel"
                  >
                    <span>Historial de importaciones</span>
                    <IconChevronRight className="w-4 h-4" />
                  </button>
                  {importHistoryOpen && (
                    <div id="imports-history-panel">
                      {importType === "account" ? (
                        imports.length ? (
                          <table className="table">
                            <thead>
                              <tr>
                                <th>Archivo</th>
                                <th>Notas</th>
                                <th>Fecha</th>
                              </tr>
                            </thead>
                            <tbody>
                              {imports.map(item => (
                                <tr key={item.id}>
                                  <td>{item.filename}</td>
                                  <td>{item.notes || "—"}</td>
                                  <td>{new Date(item.imported_at).toLocaleDateString("es-ES")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="empty-state">Todavía no importaste extractos bancarios.</div>
                        )
                      ) : cardImports.length ? (
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Archivo</th>
                              <th>Notas</th>
                              <th>Fecha</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cardImports.map(item => (
                              <tr key={item.id}>
                                <td>{item.filename}</td>
                                <td>{item.notes || "—"}</td>
                                <td>{new Date(item.imported_at).toLocaleDateString("es-ES")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="empty-state">Todavía no importaste extractos de tarjeta.</div>
                      )}
                    </div>
                  )}
                </section>
              </div>
            )}
            <div className="fixed bottom-20 lg:bottom-8 left-0 right-0 lg:left-64 px-4 lg:px-8 z-30">
              <div className="max-w-3xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg p-4">
                <div className="flex items-center justify-between gap-4">
                  <button type="button" className="btn btn-secondary" onClick={goPrevStep} disabled={currentStepIndex === 0}>
                    Anterior
                  </button>
                  <div className="text-sm text-gray-600">
                    Paso {currentStepIndex + 1} de {setupSteps.length}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={goNextStep}
                    disabled={currentStepIndex === setupSteps.length - 1}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          </div>
          </>
        )}
      </div>
      {accountPanelOpen && (
        <div className="settings-drawer-container">
          <div className="settings-drawer-backdrop settings-drawer-backdrop--soft" onClick={handleCloseAccountPanel} aria-hidden="true" />
          <aside className="settings-drawer" role="dialog" aria-modal="true" aria-label="Nueva cuenta">
            <button type="button" className="settings-drawer-close" onClick={handleCloseAccountPanel} aria-label="Cerrar panel">
              <IconX className="w-5 h-5" />
            </button>
            <form className="settings-drawer-form" onSubmit={handleAccountSubmit}>
              <p className="settings-drawer-label">{editingAccountId ? "Editar cuenta" : "Nueva cuenta"}</p>
              <h3 className="settings-drawer-title">Mis cuentas y tarjetas</h3>
              <p className="settings-drawer-subtitle">
                {editingAccountId ? "Actualiza los datos de la cuenta." : "Completa los datos para añadir una nueva cuenta."}
              </p>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Nombre
                <input
                  className="input"
                  value={accountForm.name}
                  onChange={event => setAccountForm(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Cuenta nómina"
                  required
                />
              </label>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Tipo
                <select
                  className="input"
                  value={accountForm.type}
                  onChange={event => setAccountForm(prev => ({ ...prev, type: event.target.value as AccountType }))}
                >
                  <option value="checking">Cuenta corriente</option>
                  <option value="savings">Ahorro</option>
                  <option value="cash">Efectivo</option>
                  <option value="credit_card">Tarjeta de crédito</option>
                </select>
              </label>
              <div className="settings-drawer-actions">
                <button type="submit" className="btn btn-primary" disabled={accountSubmitting}>
                  {accountSubmitting ? "Guardando..." : editingAccountId ? "Guardar cambios" : "Añadir cuenta"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseAccountPanel}>
                  Cancelar
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
      {cardPanelOpen && (
        <div className="settings-drawer-container">
          <div className="settings-drawer-backdrop settings-drawer-backdrop--soft" onClick={handleCloseCardPanel} aria-hidden="true" />
          <aside className="settings-drawer" role="dialog" aria-modal="true" aria-label="Nueva tarjeta">
            <button type="button" className="settings-drawer-close" onClick={handleCloseCardPanel} aria-label="Cerrar panel">
              <IconX className="w-5 h-5" />
            </button>
            <form className="settings-drawer-form" onSubmit={handleCardSubmit}>
              <p className="settings-drawer-label">{editingCardId ? "Editar tarjeta" : "Nueva tarjeta"}</p>
              <h3 className="settings-drawer-title">Mis cuentas y tarjetas</h3>
              <p className="settings-drawer-subtitle">
                {editingCardId ? "Actualiza los datos de la tarjeta." : "Asocia la tarjeta a su cuenta de cargo."}
              </p>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Nombre
                <input
                  className="input"
                  value={cardForm.name}
                  onChange={event => setCardForm(prev => ({ ...prev, name: event.target.value }))}
                  placeholder="Tarjeta Supermercado"
                  required
                />
              </label>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Cuenta de cargo
                <select
                  className="input"
                  value={cardForm.accountId}
                  onChange={event => {
                    const nextAccountId = event.target.value;
                    setCardForm(prev => ({
                      ...prev,
                      accountId: nextAccountId,
                    }));
                  }}
                  required
                >
                  <option value="">Selecciona una cuenta</option>
                  {activeAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name} · {account.currency}
                    </option>
                  ))}
                </select>
              </label>
              <div className="text-xs text-slate-500">
                Al guardar la tarjeta se creará automáticamente una categoría de tipo traspaso con el mismo nombre.
              </div>
              <div className="settings-drawer-actions">
                <button type="submit" className="btn btn-primary" disabled={cardSubmitting}>
                  {cardSubmitting ? "Guardando..." : editingCardId ? "Guardar cambios" : "Añadir tarjeta"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseCardPanel}>
                  Cancelar
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
      {categoryDrawer && (
        <div className="settings-drawer-container settings-drawer-container--modal">
          <div className="settings-drawer-backdrop settings-drawer-backdrop--soft" onClick={handleDrawerClose} aria-hidden="true" />
          <aside
            className="settings-drawer settings-drawer--modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={drawerHeadingId}
            aria-describedby={drawerSubtitleId}
          >
            <button type="button" className="settings-drawer-close" onClick={handleDrawerClose} aria-label="Cerrar panel">
              <IconX className="w-5 h-5" />
            </button>

            {categoryDrawer.mode === "create" ? (
              <form className="settings-drawer-form" onSubmit={handleCategorySubmit}>
                <p className="settings-drawer-label">Nueva categoría</p>
                <h3 id={drawerHeadingId} className="settings-drawer-title">Categoría rápida</h3>
                <p id={drawerSubtitleId} className="settings-drawer-subtitle">
                  Completa los campos para crear una nueva categoría e, opcionalmente, su primera regla automática.
                </p>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Nombre
                  <input
                    className="input"
                    value={categoryForm.name}
                    onChange={event => setCategoryForm(prev => ({ ...prev, name: event.target.value }))}
                    placeholder="Supermercado"
                    required
                  />
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Tipo
                  <select
                    className="input"
                    value={categoryForm.kind}
                    onChange={event => {
                      const nextKind = event.target.value as CategoryKind;
                      setCategoryForm(prev => ({
                        ...prev,
                        kind: nextKind,
                        color: prev.color === null ? null : normalizeColorForKind(nextKind, prev.color),
                      }));
                    }}
                  >
                    <option value="expense">Gasto</option>
                    <option value="income">Ingreso</option>
                    <option value="transfer">Traspasos</option>
                  </select>
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Color
                  <div className="settings-drawer-color-grid">
                    {availableCategoryColors.map(option => {
                      const isSelected = categoryForm.color?.toLowerCase() === option.toLowerCase();
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setCategoryForm(prev => ({ ...prev, color: option }))}
                          style={{
                            width: "1.85rem",
                            height: "1.85rem",
                            borderRadius: "999px",
                            border: isSelected ? "3px solid #0f172a" : "2px solid rgba(15, 23, 42, 0.15)",
                            background: option,
                            cursor: "pointer",
                            outline: "none",
                          }}
                          aria-label={`Seleccionar color ${option}`}
                        />
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setCategoryForm(prev => ({ ...prev, color: null }))}
                      className="settings-drawer-outline-button"
                    >
                      Sin color
                    </button>
                  </div>
                </label>
                <div className="settings-drawer-section">
                  <p className="settings-drawer-label" style={{ letterSpacing: 0, fontSize: "0.78rem", color: "#334155" }}>
                    Regla inicial (opcional)
                  </p>
                  <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    <span className="field-label-with-help">
                      Texto en la descripción del movimiento
                      <PatternFieldHelp text="Intentará asignar a esta categoría todos los movimientos bancarios en cuya descripción aparezcan las palabras que indiques en el orden de aparición. Interpreta tanto mayúsculas como minúsculas." />
                    </span>
                    <input
                      className="input"
                      value={categoryForm.initialRule.pattern}
                      onChange={event => setCategoryForm(prev => ({
                        ...prev,
                        initialRule: { ...prev.initialRule, pattern: event.target.value },
                      }))}
                      placeholder="Ej: supermercado compra"
                    />
                  </label>
                  <div className="settings-drawer-double">
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe mínimo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={categoryForm.initialRule.minAmount}
                        onChange={event => setCategoryForm(prev => ({
                          ...prev,
                          initialRule: { ...prev.initialRule, minAmount: event.target.value },
                        }))}
                        placeholder="Opcional"
                      />
                    </label>
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe máximo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={categoryForm.initialRule.maxAmount}
                        onChange={event => setCategoryForm(prev => ({
                          ...prev,
                          initialRule: { ...prev.initialRule, maxAmount: event.target.value },
                        }))}
                        placeholder="Opcional"
                      />
                    </label>
                  </div>
                </div>
                <div className="settings-drawer-actions">
                  <button type="submit" className="btn btn-primary" disabled={categorySubmitting}>
                    {categorySubmitting ? "Guardando..." : "Crear categoría"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handleDrawerClose}>
                    Cancelar
                  </button>
                </div>
              </form>
            ) : categoryDrawer.mode === "edit" && drawerCategory ? (
              <form className="settings-drawer-form" onSubmit={handleEditCategorySubmit}>
                <p className="settings-drawer-label">Editar categoría</p>
                <h3 id={drawerHeadingId} className="settings-drawer-title">{drawerCategory?.name ?? ""}</h3>
                <p id={drawerSubtitleId} className="settings-drawer-subtitle">
                  Actualiza el nombre, el tipo y el color de esta categoría.
                </p>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Nombre
                  <input
                    className="input"
                    value={editCategoryForm.name}
                    onChange={event =>
                      setEditCategoryForm(prev => ({ ...prev, name: event.target.value }))
                    }
                    placeholder="Nombre de la categoría"
                    required
                  />
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Tipo
                  <select
                    className="input"
                    value={editCategoryForm.kind}
                    onChange={event => {
                      const nextKind = event.target.value as CategoryKind;
                      setEditCategoryForm(prev => ({
                        ...prev,
                        kind: nextKind,
                        color: prev.color === null ? null : normalizeColorForKind(nextKind, prev.color),
                      }));
                    }}
                  >
                    <option value="expense">Gasto</option>
                    <option value="income">Ingreso</option>
                    <option value="transfer">Traspasos</option>
                  </select>
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Color
                  <div className="settings-drawer-color-grid">
                    {availableEditCategoryColors.map(option => {
                      const isSelected = editCategoryForm.color?.toLowerCase() === option.toLowerCase();
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => setEditCategoryForm(prev => ({ ...prev, color: option }))}
                          style={{
                            width: "1.85rem",
                            height: "1.85rem",
                            borderRadius: "999px",
                            border: isSelected ? "3px solid #0f172a" : "2px solid rgba(15, 23, 42, 0.15)",
                            background: option,
                            cursor: "pointer",
                            outline: "none",
                          }}
                          aria-label={`Seleccionar color ${option}`}
                        />
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setEditCategoryForm(prev => ({ ...prev, color: null }))}
                      className="settings-drawer-outline-button"
                    >
                      Sin color
                    </button>
                  </div>
                </label>
                <div className="settings-drawer-actions">
                  <button type="submit" className="btn btn-primary" disabled={categorySubmitting}>
                    {categorySubmitting ? "Guardando..." : "Guardar cambios"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={handleDrawerClose}>
                    Cancelar
                  </button>
                </div>
              </form>
            ) : categoryDrawer.mode === "rules" && drawerCategory ? (
              <div className="settings-drawer-body">
                <p className="settings-drawer-label">Reglas automáticas</p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  <h3 id={drawerHeadingId} className="settings-drawer-title">{drawerCategory?.name ?? ""}</h3>
                  <div className="settings-drawer-pill">
                    {drawerCategory ? getCategoryKindLabel(drawerCategory.kind) : ""}
                  </div>
                  <p id={drawerSubtitleId} className="settings-drawer-subtitle">
                    Gestiona las reglas que aplican automáticamente esta categoría a tus movimientos.
                  </p>
                </div>
                <div className="settings-drawer-section">
                  {drawerCategory?.kind !== "transfer" && (
                    <p className="settings-drawer-info" style={{ marginBottom: "0.1rem" }}>
                      Esta categoría solo admite importes {drawerCategory?.kind === "income" ? "positivos" : "negativos"}.
                    </p>
                  )}
                  {rulesForSelectedCategory.length ? (
                    <div className="settings-drawer-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Texto en descripción</th>
                            <th>Rango de importes</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rulesForSelectedCategory.map(rule => (
                            <tr key={rule.id}>
                              <td><code>{rule.pattern}</code></td>
                              <td>{formatRuleRange(rule)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  style={{ padding: "0.3rem 0.6rem", fontSize: "0.78rem" }}
                                  onClick={() => handleRuleDelete(rule.id)}
                                  disabled={ruleDeletingId === rule.id}
                                >
                                  {ruleDeletingId === rule.id ? "Eliminando..." : "Eliminar"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="settings-drawer-empty">Todavía no definiste reglas para esta categoría.</div>
                  )}
                </div>
                <form className="settings-drawer-form" onSubmit={handleRuleSubmit}>
                  <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                    <span className="field-label-with-help">
                      Texto en la descripción del movimiento
                      <PatternFieldHelp text="Intentará asignar a esta categoría todos los movimientos bancarios en cuya descripción aparezcan las palabras que indiques en el orden de aparición. Interpreta tanto mayúsculas como minúsculas." />
                    </span>
                    <input
                      className="input"
                      value={ruleForm.pattern}
                      onChange={event => setRuleForm(prev => ({ ...prev, pattern: event.target.value }))}
                      placeholder="Ej: supermercado compra"
                      required
                    />
                  </label>
                  <div className="settings-drawer-double">
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe mínimo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={ruleForm.minAmount}
                        onChange={event => setRuleForm(prev => ({ ...prev, minAmount: event.target.value }))}
                        placeholder="Opcional"
                      />
                    </label>
                    <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                      Importe máximo
                      <input
                        className="input"
                        type="number"
                        step="0.01"
                        value={ruleForm.maxAmount}
                        onChange={event => setRuleForm(prev => ({ ...prev, maxAmount: event.target.value }))}
                        placeholder="Opcional"
                      />
                    </label>
                  </div>
                  <div className="settings-drawer-actions">
                    <button type="submit" className="btn btn-primary" disabled={ruleSubmitting}>
                      {ruleSubmitting ? "Guardando..." : "Añadir regla"}
                    </button>
                    <p className="settings-drawer-info">
                      Usa * como comodín para una cadena completa y ? para un solo carácter.
                    </p>
                  </div>
                </form>
              </div>
            ) : (
              <div className="settings-drawer-body">
                <p className="settings-drawer-info">Selecciona una categoría válida para continuar.</p>
                <div className="settings-drawer-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleDrawerClose}>
                    Cerrar
                  </button>
                </div>
              </div>
            )}
          </aside>
        </div>
      )}
      {manualTransactionModalOpen && (
        <div className="settings-drawer-container settings-drawer-container--modal">
          <div className="settings-drawer-backdrop settings-drawer-backdrop--soft" onClick={() => setManualTransactionModalOpen(false)} aria-hidden="true" />
          <aside className="settings-drawer settings-drawer--modal" role="dialog" aria-modal="true" aria-label="Registrar movimiento manual">
            <button
              type="button"
              className="settings-drawer-close"
              onClick={() => setManualTransactionModalOpen(false)}
              aria-label="Cerrar panel"
            >
              <IconX className="w-5 h-5" />
            </button>
            <form onSubmit={handleTransactionSubmit} className="settings-drawer-form">
              <p className="settings-drawer-label">Movimiento manual</p>
              <h3 className="settings-drawer-title">Registrar movimiento</h3>
              <p className="settings-drawer-subtitle">Añade un movimiento puntual sin importar un extracto.</p>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Tipo de movimiento
                <select
                  className="input"
                  value={manualMovementType}
                  onChange={event => setManualMovementType(event.target.value as "account" | "card")}
                >
                  <option value="account">Cuenta bancaria</option>
                  <option value="card">Tarjeta</option>
                </select>
              </label>
              {manualMovementType === "account" ? (
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Cuenta
                <select
                  className="input"
                  value={transactionForm.accountId}
                  onChange={event => setTransactionForm(prev => ({ ...prev, accountId: event.target.value }))}
                  required
                >
                  <option value="">Selecciona cuenta</option>
                  {activeAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </label>
              ) : (
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Tarjeta
                  <select
                    className="input"
                    value={manualCardId}
                    onChange={event => setManualCardId(event.target.value)}
                    required
                  >
                    <option value="">Selecciona tarjeta</option>
                    {activeCards.map(card => (
                      <option key={card.id} value={card.id}>
                        {card.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              {manualMovementType === "account" && (
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Categoría
                <select
                  className="input"
                  value={transactionForm.categoryId}
                  onChange={event => setTransactionForm(prev => ({ ...prev, categoryId: event.target.value }))}
                >
                  <option value="">Sin categoría</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </label>
              )}
              <div className="settings-drawer-double">
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Fecha
                  <input
                    className="input"
                    type="date"
                    value={transactionForm.transactionDate}
                    onChange={event => setTransactionForm(prev => ({ ...prev, transactionDate: event.target.value }))}
                    required
                  />
                </label>
                <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                  Importe
                  <input
                    className="input"
                    type="text"
                    value={transactionForm.amount}
                    onChange={event => setTransactionForm(prev => ({ ...prev, amount: event.target.value }))}
                    placeholder="Negativo para gastos"
                    required
                  />
                </label>
              </div>
              <label style={{ fontWeight: 600, color: "#334155", display: "flex", flexDirection: "column", gap: "0.35rem" }}>
                Descripción
                <input
                  className="input"
                  value={transactionForm.description}
                  onChange={event => setTransactionForm(prev => ({ ...prev, description: event.target.value }))}
                  placeholder="Descripción opcional"
                />
              </label>
              <div className="settings-drawer-actions">
                <button type="submit" className="btn btn-primary" disabled={transactionSubmitting}>
                  {transactionSubmitting ? "Guardando..." : "Registrar movimiento"}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setManualTransactionModalOpen(false)}>
                  Cancelar
                </button>
              </div>
            </form>
          </aside>
        </div>
      )}
      <PlanLimitModal
        open={Boolean(planLimitDetails)}
        details={planLimitDetails}
        onClose={() => setPlanLimitDetails(null)}
        onUpgradeMonthly={handleUpgradeMonthly}
        onUpgradeYearly={handleUpgradeYearly}
        loading={billingActionLoading === "month" || billingActionLoading === "year"}
      />
      </div>
    </DashboardLayout>
  );
}
